import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { supabase } from "../../supabaseClient";
import { FONTS } from "./landingTheme";
import { useBackClose } from "../../Helpers/useBackClose";

/* ─────────────────────── AUTH ICONS ─────────────────────── */
const GoogleLogo = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
);

const MicrosoftLogo = () => (
    <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="9" height="9" fill="#f25022" />
        <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
        <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
        <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
);

/* ─────────────────────── MODAL BASE (login e contato) ─────────────────────── */
export const LandingModal = ({ isOpen, onClose, colors, children }) => {
    useBackClose(isOpen, onClose);
    return (
    <AnimatePresence>
        {isOpen && (
            <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={onClose}
                    style={{
                        position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                        background: "rgba(0,0,0,0.6)", backdropFilter: "blur(10px)",
                    }}
                />

                {/* Modal Container */}
                <motion.div
                    role="dialog"
                    aria-modal="true"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
                    style={{
                        position: "relative", width: "calc(100% - 24px)", maxWidth: 480, background: colors.bg,
                        borderRadius: 24, padding: 40, boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                        boxSizing: "border-box", fontFamily: FONTS.body,
                    }}
                >
                    {/* Botão Fechar: área de toque de 44px, com o ✕ no mesmo lugar de antes */}
                    <button onClick={onClose} aria-label="Fechar" style={{ position: "absolute", top: 8, right: 8, width: 44, height: 44, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", background: "transparent", border: "none", fontSize: 20, color: colors.textMuted, cursor: "pointer" }}>
                        ✕
                    </button>
                    {children}
                </motion.div>
            </div>
        )}
    </AnimatePresence>
    );
};

// Botão em pílula dos modais (mesmo formato do "Continuar com Google")
export const modalButtonStyle = (colors) => ({
    display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
    width: "100%", padding: "14px", borderRadius: 50, border: "1px solid rgba(120,120,120,0.3)",
    background: "transparent", color: colors.textDark, fontFamily: FONTS.body, fontWeight: 600, fontSize: 15,
    cursor: "pointer", transition: "background 0.2s", boxSizing: "border-box", textDecoration: "none",
});
const hoverOn = (e) => { e.currentTarget.style.background = "rgba(120,120,120,0.1)"; };
const hoverOff = (e) => { e.currentTarget.style.background = "transparent"; };
export const modalButtonHover = { onMouseEnter: hoverOn, onMouseLeave: hoverOff };

/* ─────────────────────── AUTH MODAL ─────────────────────── */
const AuthModal = ({ isOpen, onClose, colors }) => {
    const handleGoogleLogin = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: { redirectTo: window.location.origin + '/painel' }
        });
        if (error) alert("Erro ao fazer login com Google: " + error.message);
    };

    const handleMicrosoftLogin = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'azure',
            options: { redirectTo: window.location.origin + '/painel' }
        });
        if (error) alert("Erro ao fazer login com Microsoft: " + error.message);
    };

    return (
        <LandingModal isOpen={isOpen} onClose={onClose} colors={colors}>
            {/* Cabeçalho */}
            <div style={{ textAlign: "center", color: colors.textDark, marginBottom: 32 }}>
                <h2 style={{ fontFamily: FONTS.heading, fontSize: 24, margin: "0 0 16px", lineHeight: 1.3 }}>
                    Máquinas online<br />Faturamento contínuo
                </h2>
                <p style={{ fontSize: 14, color: colors.textMuted, margin: 0, lineHeight: 1.5 }}>
                    Acesse a plataforma e assuma o<br />controle inteligente da sua operação.
                </p>
            </div>

            {/* Botões OAuth */}
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 24 }}>
                <button onClick={handleGoogleLogin} style={modalButtonStyle(colors)} {...modalButtonHover}>
                    <GoogleLogo /> Continuar com Google
                </button>
                <button onClick={handleMicrosoftLogin} style={modalButtonStyle(colors)} {...modalButtonHover}>
                    <MicrosoftLogo /> Continuar com Microsoft
                </button>
            </div>

            <p style={{ fontSize: 12, color: colors.textDark, opacity: 0.7, margin: 0, textAlign: "center", lineHeight: 1.5 }}>
                Ao entrar, você concorda com os{" "}
                <a href="/termos" target="_blank" rel="noopener noreferrer" style={{ color: colors.purpleDark, fontWeight: 600 }}>
                    Termos de Uso e a Política de Privacidade
                </a>.
            </p>
        </LandingModal>
    );
};

export default AuthModal;

